import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, ref as dbRef, set, serverTimestamp, get } from '../firebase';
import { storage, storageRef, uploadBytesResumable, getDownloadURL } from '../firebase';
import { FiUpload, FiX, FiPlay, FiImage } from 'react-icons/fi';

const VideoUploading = () => {
  const [formData, setFormData] = useState({
    videoFile: null,
    thumbnailFile: null,
    title: '',
    description: '',
    category: 'video',
    privacy: 'public',
  });
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({ video: 0, thumbnail: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState({ video: null, thumbnail: null });
  const [userPlan, setUserPlan] = useState(null);
  const videoRef = useRef(null);
  const errorRef = useRef(null);
  const navigate = useNavigate();

  // Fetch user plan from Firebase
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('currentUser'));
        if (!userData || !userData.mobile) {
          setErrors({ submit: 'Please log in to upload a video.' });
          navigate('/login');
          return;
        }
        const sanitizedMobile = userData.mobile;

        const planRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}/plan`);
        const snapshot = await get(planRef);
        if (snapshot.exists()) {
          setUserPlan(snapshot.val());
        } else {
          setUserPlan('unknown');
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlan('unknown');
      }
    };

    fetchUserPlan();
  }, [navigate]);

  // Get plan message based on userPlan
  const getPlanMessage = () => {
    if (!userPlan) return 'Fetching plan...';
    switch (userPlan.toLowerCase()) {
      case 'yearly':
        return 'Your plan is Yearly. You can upload photos and videos only one time.';
      case 'unlimited':
        return 'Your plan is Unlimited. You can upload photos and videos unlimited times one Year.';
      default:
        return 'Your plan is not recognized. Please contact support.';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.videoFile) {
      newErrors.videoFile = 'Video file is required';
    } else {
      const validVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/mkv'];
      if (!validVideoTypes.includes(formData.videoFile.type)) {
        newErrors.videoFile = 'Invalid video format (.mp4, .avi, .mov, .mkv only)';
      } else if (formData.videoFile.size > 500 * 1024 * 1024) {
        newErrors.videoFile = 'Video file size must be less than 500MB';
      }
    }
    if (!formData.thumbnailFile) {
      newErrors.thumbnailFile = 'Thumbnail image is required';
    } else {
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validImageTypes.includes(formData.thumbnailFile.type)) {
        newErrors.thumbnailFile = 'Invalid image format (.jpg, .jpeg, .png only)';
      }
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      const url = URL.createObjectURL(files[0]);
      setPreviewUrl((prev) => ({ ...prev, [name === 'videoFile' ? 'video' : 'thumbnail']: url }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      if (errors.submit && errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const userData = JSON.parse(localStorage.getItem('currentUser'));
    if (!userData || !userData.mobile) {
      setErrors({ submit: 'Please log in to upload a video.' });
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      navigate('/login');
      return;
    }

    const sanitizedMobile = userData.mobile;

    // Check user plan and upload restrictions
    if (!userPlan || userPlan.toLowerCase() === 'unknown') {
      setErrors({ submit: 'Your plan is not recognized. Please contact support.' });
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (userPlan.toLowerCase() === 'yearly') {
      try {
        const videosRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}`);
        const videosSnapshot = await get(videosRef);
        if (videosSnapshot.exists() && Object.keys(videosSnapshot.val()).length > 0) {
          alert('Your plan is Yearly. You can only upload one video. Please upgrade your plan to upload more.');
          return;
        }
      } catch (error) {
        console.error('Error checking video count:', error);
        setErrors({ submit: 'Failed to verify upload limit. Please try again.' });
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }

    setIsUploading(true);

    try {
      // Fetch current number of uploaded videos for numbering
      const videosRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}`);
      const videosSnapshot = await get(videosRef);
      const uploadCount = videosSnapshot.exists() ? Object.keys(videosSnapshot.val()).length + 1 : 1;
      const videoId = `${sanitizedMobile}_video_${uploadCount}`;

      // Upload video
      const videoStoragePath = `videoplayer/video/${sanitizedMobile}_${uploadCount}.${formData.videoFile.name.split('.').pop()}`;
      const videoStorageRef = storageRef(storage, videoStoragePath);
      const videoUploadTask = uploadBytesResumable(videoStorageRef, formData.videoFile);

      videoUploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({ ...prev, video: progress }));
        },
        (error) => {
          throw new Error('Video upload failed: ' + error.message);
        }
      );

      // Upload thumbnail
      const thumbnailStoragePath = `videoplayer/video/thumbnails/${sanitizedMobile}_${uploadCount}.${formData.thumbnailFile.name.split('.').pop()}`;
      const thumbnailStorageRef = storageRef(storage, thumbnailStoragePath);
      const thumbnailUploadTask = uploadBytesResumable(thumbnailStorageRef, formData.thumbnailFile);

      thumbnailUploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({ ...prev, thumbnail: progress }));
        },
        (error) => {
          throw new Error('Thumbnail upload failed: ' + error.message);
        }
      );

      // Wait for both uploads to complete
      const [videoSnapshot, thumbnailSnapshot] = await Promise.all([
        videoUploadTask,
        thumbnailUploadTask,
      ]);

      const videoURL = await getDownloadURL(videoSnapshot.ref);
      const thumbnailURL = await getDownloadURL(thumbnailSnapshot.ref);

      // Save metadata to Realtime Database
      const videoDbRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoId}`);
      await set(videoDbRef, {
        id: videoId,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        videoURL,
        thumbnailURL,
        privacy: formData.privacy,
        uploadDate: serverTimestamp(),
        channelEmail: userData.email || sanitizedMobile,
        views: 0,
        allowComments: true,
        isForKids: false,
        status: 'pending',
      });

      alert('Video uploaded successfully!');
      navigate('/home');
    } catch (error) {
      console.error('Upload Error:', error);
      setErrors({ submit: 'An error occurred while uploading. Please try again.' });
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } finally {
      setIsUploading(false);
      if (previewUrl.video) {
        URL.revokeObjectURL(previewUrl.video);
      }
      if (previewUrl.thumbnail) {
        URL.revokeObjectURL(previewUrl.thumbnail);
      }
      setPreviewUrl({ video: null, thumbnail: null });
    }
  };

  const handleCancel = () => {
    if (previewUrl.video) {
      URL.revokeObjectURL(previewUrl.video);
    }
    if (previewUrl.thumbnail) {
      URL.revokeObjectURL(previewUrl.thumbnail);
    }
    setPreviewUrl({ video: null, thumbnail: null });
    navigate('/home');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-gray-200 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 w-full max-w-md">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 text-center flex items-center">
          <FiUpload className="mr-3 text-indigo-600 text-3xl" /> Upload Video
        </h2>
        <div className="bg-indigo-50 text-indigo-700 p-3 rounded-lg mb-4 text-sm border border-indigo-200">
          {getPlanMessage()}
        </div>
        {errors.submit && (
          <div ref={errorRef} className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm border border-red-200">
            {errors.submit}
          </div>
        )}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiPlay className="mr-2 text-indigo-600" /> Video File <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="file"
              name="videoFile"
              accept=".mp4,.avi,.mov,.mkv"
              onChange={handleFileChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {errors.videoFile && <p className="text-red-500 text-xs mt-1">{errors.videoFile}</p>}
            {previewUrl.video && (
              <video
                ref={videoRef}
                src={previewUrl.video}
                controls
                className="mt-4 w-full rounded-lg shadow-md"
                style={{ maxHeight: '300px' }}
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiImage className="mr-2 text-indigo-600" /> Thumbnail Image <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="file"
              name="thumbnailFile"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {errors.thumbnailFile && <p className="text-red-500 text-xs mt-1">{errors.thumbnailFile}</p>}
            {previewUrl.thumbnail && (
              <img
                src={previewUrl.thumbnail}
                alt="Thumbnail Preview"
                className="mt-4 w-full rounded-lg shadow-md"
                style={{ maxHeight: '300px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter video title"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter video description"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows="4"
            />
          </div>
          
          {isUploading && (
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-700">Video Upload Progress: {Math.round(uploadProgress.video)}%</p>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full"
                    style={{ width: `${uploadProgress.video}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-700">Thumbnail Upload Progress: {Math.round(uploadProgress.thumbnail)}%</p>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full"
                    style={{ width: `${uploadProgress.thumbnail}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
          <div className="flex space-x-4 w-full">
            <button
              onClick={handleCancel}
              className="w-1/2 bg-gray-600 text-white p-3 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-300 flex items-center justify-center"
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isUploading}
              className="w-1/2 bg-indigo-600 text-white p-3 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 flex items-center justify-center disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <span className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                <>
                  <FiUpload className="mr-2" /> Upload
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploading;